import { useEffect, useState } from "react";
import { History, ChevronDown } from "lucide-react";
import { api, inr, sign } from "@/lib/api";

const KIND = { start: "text-emerald-300", resume: "text-amber-300", restart: "text-slate-400", fail: "text-red-300", lost: "text-red-400" };
const secs = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

// Last 7 days: when the engine started, server restarts, run resumes (downtime / back in), failures
export function EngineDayLog() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const load = () => api.get("/engine/log?days=7").then((r) => setRows(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);
  if (!rows) return null;
  return (
    <div className="panel p-3" data-testid="engine-day-log">
      <div className="flex items-center gap-2 mb-2"><History size={14} className="text-cyan-300" /><span className="label-xs">Engine day log</span></div>
      {!rows.length && <p className="text-xs text-slate-500" data-testid="engine-log-empty">Nothing logged yet — entries appear when the engine starts, the server restarts or a run is resumed.</p>}
      <div className="space-y-1">
        {rows.map((d) => {
          const back = d.back_s.length ? Math.max(...d.back_s) : null;
          return (
            <div key={d.date} className="rounded-md border border-[#1E293B]" data-testid={`engine-log-${d.date}`}>
              <button onClick={() => setOpen(open === d.date ? null : d.date)} className="w-full grid grid-cols-2 md:grid-cols-[100px_1fr_1fr_1fr_1fr_auto] gap-x-3 gap-y-0.5 px-3 py-2 text-xs num text-left hover:bg-[#111827]">
                <span className="text-slate-200 font-semibold">{d.date.slice(5)}</span>
                <span className="text-slate-400">start <span className="text-slate-100">{d.started || "—"}</span></span>
                <span className="text-slate-400">restarts <span className={d.restarts ? "text-amber-300" : "text-slate-100"}>{d.restarts}</span></span>
                <span className="text-slate-400">resumes <span className={d.resumes ? "text-amber-300" : "text-slate-100"}>{d.resumes}</span>{d.resumes ? ` · down ${secs(d.down_s)}${back !== null ? ` · back in ${secs(back)}` : ""}` : ""}</span>
                <span className="text-slate-400 truncate">{d.last ? <>{d.last.mode?.toUpperCase()} · <span className={sign(d.last.net || 0)}>{inr(d.last.net || 0)}</span> · {d.last.status}</> : d.fails || d.lost ? <span className="text-red-300">{d.fails} failed · {d.lost} lost</span> : "no run"}</span>
                <ChevronDown size={13} className={`text-slate-500 justify-self-end transition-transform ${open === d.date ? "rotate-180" : ""}`} />
              </button>
              {open === d.date && (
                <div className="px-3 pb-2 space-y-0.5" data-testid={`engine-log-events-${d.date}`}>
                  {d.events.map((e, i) => <div key={i} className="text-[11px] num flex gap-2"><span className="text-slate-500">{e.at}</span><span className={KIND[e.kind] || "text-slate-300"}>{e.kind}</span><span className="text-slate-300">{e.msg}{e.ready_s != null ? ` · back in ${secs(e.ready_s)}` : ""}</span></div>)}
                  {d.last?.reason && <div className="text-[11px] text-slate-400">Run: {d.last.reason}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
